import { on } from '@ember/modifier';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { dropTask } from 'ember-concurrency';
import perform from 'ember-concurrency/helpers/perform';
import FocusTrapper from 'ember-focus-trapper/components/focus-trapper';
import set from 'ember-set-helper/helpers/set';
import { not, or } from 'ember-truth-helpers';
import ButtonGroup from 'garaje/components/ui/button-group';
import ModalDialog from 'garaje/components/ui/classic/modal-dialog';
import UiButton from 'garaje/components/ui-button';
import UiInputField from 'garaje/components/ui-input-field';
import type MailerTemplateModel from 'garaje/models/mailer-template';

interface CreateMailerTemplateModalSignature {
  Args: {
    mailerTemplate: MailerTemplateModel;
    onClose: () => void;
    onSave: (title: string) => Promise<void>;
  };
}

export default class CreateMailerTemplateModal extends Component<CreateMailerTemplateModalSignature> {
  @tracked title = '';

  get isValidTitle(): boolean {
    return this.title.trim().length > 0;
  }

  submitTask = dropTask(async (e: Event): Promise<void> => {
    e.preventDefault();
    await this.args.onSave(this.title.trim());
  });

  <template>
    <ModalDialog @clickOutsideToClose={{true}} @translucentOverlay={{true}} @onClose={{@onClose}} ...attributes>
      <FocusTrapper>
        <form data-test-create-mailer-template-modal class="p-8" {{on "submit" (perform this.submitTask)}}>
          <div class="flex flex-row items-center mb-4">
            <h3 data-test-create-mailer-template-heading class="flex-1">New {{@mailerTemplate.title}} email</h3>
            <UiButton
              type="button"
              aria-label="Close"
              data-test-create-mailer-template-close-button
              class="btn btn--square btn--outlineFill !bg-white icon-cross-small-gray email"
              name="close"
              {{on "click" @onClose}}
            />
          </div>
          <UiInputField as |Field|>
            <Field.label @required={{true}}>Template name</Field.label>
            <Field.input
              data-test-create-mailer-template-title-input
              placeholder="My custom template"
              @value={{this.title}}
              @update={{set this "title"}}
            />
          </UiInputField>
          <footer class="pt-8 flex justify-end">
            <ButtonGroup @reverse={{true}}>
              <button
                data-test-create-mailer-template-save-button
                class="btn btn--small btn--brand"
                disabled={{or this.submitTask.isRunning (not this.isValidTitle)}}
              >
                {{if this.submitTask.isRunning "Saving..." "Save and continue"}}
              </button>
            </ButtonGroup>
          </footer>
        </form>
      </FocusTrapper>
    </ModalDialog>
  </template>
}
